import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Chip, LinearProgress, Table, TableBody, TableCell, TableContainer, TableRow, Paper, IconButton, Button } from '@mui/material';
import {
    Business,
    Subject,
    Person,
    Description,
    AssignmentInd,
    People,
    Timeline as TimelineIcon,
    LabelImportant,
    Code,
    Flag,
    StackedBarChart,
    Comment,
    CheckCircle,
    Close, TaskAlt
} from '@mui/icons-material';
import { localAxiosGet } from '../../../Axios/axios';
import axios from 'axios';

const priorityColors = {
    High: 'error',
    Medium: 'warning',
    Low: 'success',
    default: 'default',
};

const statusColors = {
    Pending: 'warning',
    'In Progress': 'primary',
    Completed: 'success',
    Blocked: 'error',
};

const TaskDetails = ({ taskId, onClose }) => {
    const [taskDetails, setTaskDetails] = useState({});
    const accessToken = localStorage.getItem('accessToken');

    useEffect(() => {
        const fetchTaskDetails = async () => {
            try {
                const response = await axios(localAxiosGet(`/task/getManagerTaskById?task_id=${taskId}`,accessToken));
                console.log('task details', response.data);
                setTaskDetails(response.data.task);
            } catch (error) {
                console.log('Error in fetching the task details', error);
            }
        };
        if (taskId) {
            fetchTaskDetails();
        }
    }, [taskId]);

    if (!taskId) {
        return null;
    }

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                p: 2,
                borderRadius: '12px',
                color: 'white',
                justifyContent: 'space-between'
            }}>
                <Typography variant="h5" component="span" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TaskAlt /> 
                    Task Details
                </Typography>

            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
              
                <TableContainer component={Paper} sx={{ width: '50%' }}>
                    <Table aria-label="main task details">
                        <TableBody>
                            <TableRow sx={{ '& td, & th': { py: 1.2 } }}> 
                                <TableCell><Subject sx={{ color: '#64b5f6' }} /></TableCell>
                                <TableCell>Task Name</TableCell>
                                <TableCell>{taskDetails?.task_title || '-'}</TableCell>
                            </TableRow>
                            <TableRow sx={{ '& td, & th': { py: 1.2 } }}>
                                <TableCell><Business sx={{ color: '#1976d2' }} /></TableCell>
                                <TableCell>Project Name</TableCell>
                                <TableCell>{taskDetails?.project_name || '-'}</TableCell>
                            </TableRow>
                            <TableRow sx={{ '& td, & th': { py: 1.2 } }}>
                                <TableCell><Flag sx={{ color: priorityColors[taskDetails?.priority] || '#757575' }} /></TableCell>
                                <TableCell>Priority</TableCell>
                                <TableCell>
                                    <Chip
                                        label={taskDetails?.priority || 'Not Set'}
                                        color={priorityColors[taskDetails?.priority] || 'default'}
                                        size="small"
                                        variant="outlined"
                                        sx={{ padding: '2px' }}
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow sx={{ '& td, & th': { py: 1.2 } }}>
                                <TableCell><CheckCircle sx={{ color: statusColors[taskDetails?.status] || '#757575' }} /></TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>
                                    <Chip
                                        label={taskDetails?.status || 'Not Set'}
                                        color={statusColors[taskDetails?.status] || 'default'}
                                        size="small"
                                        variant="outlined"
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow sx={{ '& td, & th': { py: 1.2 } }}>
                                <TableCell><StackedBarChart sx={{ color: '#757575', fontSize: 'small' }} /></TableCell>
                                <TableCell>Progress</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LinearProgress variant="determinate" value={taskDetails?.progress || 0} size="small" sx={{ width: 80 }} />
                                        <Typography variant="body2">{taskDetails?.progress}%</Typography>

                                    </Box>
                                </TableCell>
                            </TableRow>
                            <TableRow sx={{ '& td, & th': { py: 1.2 } }}>
                                <TableCell>{taskDetails?.development_type ? <Code sx={{ color: '#f57c00' }} /> : <AssignmentInd sx={{ color: '#2e7d32' }} />}</TableCell>
                                <TableCell>Task Type</TableCell>
                                <TableCell>{taskDetails?.task_type || '-'}{taskDetails?.development_type && ` (${taskDetails.development_type})`}</TableCell>
                            </TableRow>
                            <TableRow sx={{ '& td, & th': { py: 1.2 } }}>
                                <TableCell>{taskDetails?.assign_type === 'Single' ? <Person sx={{ color: '#3f51b5' }} /> : <People sx={{ color: '#3f51b5' }} />}</TableCell>
                                <TableCell>Assignment Type</TableCell>
                                <TableCell>{taskDetails?.assign_type === 'Single' ? 'Single Member' : 'Multiple Members'}</TableCell>
                            </TableRow>
                            <TableRow sx={{ '& td, & th': { py: 1.2 } }}>
                                <TableCell><Person sx={{ color: '#3f51b5' }} /></TableCell>
                                <TableCell>Assignee(s) / Team Members</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {taskDetails?.assignees?.map((assignee) => (
                                            <Chip
                                                key={`${assignee.first_name}-${assignee.last_name}`}
                                                label={`${assignee.first_name} ${assignee.last_name}`}
                                                color="primary"
                                                variant="outlined"
                                                size="small"
                                            />
                                        ))}
                                        {taskDetails?.assignees?.length === 0 && <Typography color="textSecondary" variant="caption">Not Assigned</Typography>}
                                    </Box>
                                </TableCell>
                            </TableRow>
                            <TableRow sx={{ '& td, & th': { py: 1.2 } }}>
                                <TableCell><Description sx={{ color: '#757575' }} /></TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>{taskDetails?.description || '-'}</TableCell>
                            </TableRow>
                            {taskDetails?.comment && (
                                <TableRow sx={{ '& td, & th': { py: 1.2 } }}>
                                    <TableCell><Comment sx={{ color: '#757575' }} /></TableCell>
                                    <TableCell>Comment</TableCell>
                                    <TableCell>{taskDetails.comment || '-'}</TableCell>
                                </TableRow>
                            )}
                            <TableRow sx={{ '& td, & th': { py: 1.2 } }}>
                                <TableCell><TimelineIcon sx={{ color: '#9c27b0' }} /></TableCell>
                                <TableCell>Timeline</TableCell>
                                <TableCell>
                                    {taskDetails?.start_date ? new Date(taskDetails.start_date).toLocaleDateString() : '-'} to {taskDetails?.due_date ? new Date(taskDetails.due_date).toLocaleDateString() : '-'}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

              
                <Paper sx={{ width: '50%', padding: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Subject sx={{ color: '#009688' }} />
                        <Typography variant="h6">Subtasks</Typography>
                    </Box>
                    {taskDetails?.subtasks?.length > 0 ? (
                        taskDetails.subtasks.map((subtask) => (
                            <Paper key={subtask.sub_task_id} sx={{ padding: 2, mb: 1, border: '1px solid #f0f0f0' }}>
                                <Typography variant="subtitle1" gutterBottom>{subtask.task_title}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Chip label={subtask.status} color={statusColors[subtask.status] || 'default'} size="small" variant="outlined" />
                                    <Chip label={subtask.priority} color={priorityColors[subtask.priority] || 'default'} size="small" variant="outlined" />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <StackedBarChart sx={{ color: '#757575', fontSize: 'small' }} />
                                    <LinearProgress variant="determinate" value={subtask.progress} size="small" sx={{ width: 80 }} />
                                    <Typography variant="body2">{subtask.progress}%</Typography>
                                </Box>
                                {subtask.comment && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Comment:</Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{subtask.comment}</Typography>
                                    </Box>
                                )}
                                {subtask.description && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Description:</Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{subtask.description}</Typography>
                                    </Box>
                                )}
                            </Paper>
                        ))
                    ) : (
                        <Typography variant="body2">No subtasks are there for this task.</Typography>
                    )}
                </Paper>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button variant='outlined' color='primary' onClick={onClose}>
                    CLOSE
                </Button>
            </Box>
        </Box>
    );
}

export default TaskDetails;